import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactPlayer from 'react-player';
import { Box, Typography, Chip, Stack } from '@mui/material';
import { alpha } from '@mui/material/styles';
import Iconify from '../../../../components/iconify';
import { getStreamingLink } from '../../../../services/dataCaptureService';
import { useTimeline } from '../TimelineContext';
import { globalToLocalTime, activeRecordingsAtTime } from '../timelineSync';

export default function UnifiedVideoWidget({ recordings = [], recordingMetadata = {} }) {
  const { currentTime, isPlaying, seek } = useTimeline();
  const [urlCache, setUrlCache] = useState({}); // { recordingId: url }
  const [loadingIds, setLoadingIds] = useState(new Set());
  const [errorIds, setErrorIds] = useState(new Set());
  const [active, setActive] = useState(true);
  const playerRef = useRef(null);
  const seekingRef = useRef(false);
  const containerRef = useRef(null);
  const prevActiveRecId = useRef(null);

  // Filter to recordings that have video (not sensorsOnly)
  const videoRecordings = useMemo(() => recordings.filter((r) => !r.sensorsOnly), [recordings]);

  // Determine which recording is active at the current time
  const activeRecs = useMemo(
    () => activeRecordingsAtTime(videoRecordings, currentTime),
    [videoRecordings, currentTime]
  );
  const activeRec = activeRecs[0] || null;

  // Pre-fetch streaming URLs for all video recordings
  useEffect(() => {
    videoRecordings.forEach((rec) => {
      if (urlCache[rec.id] || loadingIds.has(rec.id) || errorIds.has(rec.id)) return;
      setLoadingIds((prev) => new Set([...prev, rec.id]));
      getStreamingLink(rec.id)
        .then((link) => {
          setUrlCache((prev) => ({ ...prev, [rec.id]: link }));
          setLoadingIds((prev) => {
            const next = new Set(prev);
            next.delete(rec.id);
            return next;
          });
        })
        .catch(() => {
          setErrorIds((prev) => new Set([...prev, rec.id]));
          setLoadingIds((prev) => {
            const next = new Set(prev);
            next.delete(rec.id);
            return next;
          });
        });
    });
  }, [videoRecordings]); // eslint-disable-line react-hooks/exhaustive-deps

  // Seek player when the active recording changes or timeline is scrubbed
  useEffect(() => {
    if (!activeRec || !playerRef.current || seekingRef.current) return;
    const localTime = globalToLocalTime(currentTime, activeRec);
    if (localTime !== null) {
      // If we switched recordings, always seek
      if (prevActiveRecId.current !== activeRec.id) {
        prevActiveRecId.current = activeRec.id;
        // Small delay to let ReactPlayer load the new source
        setTimeout(() => {
          if (playerRef.current) {
            playerRef.current.seekTo(localTime / 1000, 'seconds');
          }
        }, 100);
      } else if (!isPlaying) {
        playerRef.current.seekTo(localTime / 1000, 'seconds');
      }
    }
  }, [currentTime, isPlaying, activeRec]);

  const handleProgress = useCallback(
    ({ playedSeconds }) => {
      if (!isPlaying || !activeRec) return;
      const globalTime = activeRec.startTime + playedSeconds * 1000;
      seekingRef.current = true;
      seek(globalTime);
      seekingRef.current = false;
    },
    [isPlaying, activeRec, seek]
  );

  const handleError = useCallback(() => {
    if (activeRec) {
      setErrorIds((prev) => new Set([...prev, activeRec.id]));
    }
  }, [activeRec]);

  const handleRetry = useCallback((recId) => {
    setErrorIds((prev) => {
      const next = new Set(prev);
      next.delete(recId);
      return next;
    });
    setUrlCache((prev) => {
      const next = { ...prev };
      delete next[recId];
      return next;
    });
  }, []);

  if (videoRecordings.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography color="text.secondary" variant="body2">
          No video recordings in workspace
        </Typography>
      </Box>
    );
  }

  if (!active) {
    return (
      <Box
        ref={containerRef}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          cursor: 'pointer',
          bgcolor: 'grey.100',
        }}
        onClick={() => setActive(true)}
      >
        <Iconify icon="eva:play-circle-outline" width={48} sx={{ color: 'text.secondary', mb: 1 }} />
        <Typography variant="caption" color="text.secondary">
          Click to activate
        </Typography>
      </Box>
    );
  }

  // No recording active at current time
  if (!activeRec) {
    return (
      <Box
        ref={containerRef}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          bgcolor: 'grey.50',
        }}
      >
        <Iconify icon="eva:video-off-outline" width={36} sx={{ color: 'text.disabled', mb: 1 }} />
        <Typography color="text.secondary" variant="body2">
          No video at this time
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5 }}>
          Scrub the timeline to a recording
        </Typography>
      </Box>
    );
  }

  const activeUrl = urlCache[activeRec.id];
  const isLoading = loadingIds.has(activeRec.id);
  const hasError = errorIds.has(activeRec.id);
  const meta = recordingMetadata[activeRec.id];
  const recColor = meta?.color || '#1976d2';
  const recLabel = meta?.label || activeRec.name || 'Unknown';
  const localTime = globalToLocalTime(currentTime, activeRec);
  const isInRange = localTime !== null;

  return (
    <Box
      ref={containerRef}
      sx={{ width: '100%', height: '100%', position: 'relative', display: 'flex', flexDirection: 'column' }}
    >
      {/* Recording indicator bar */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{
          px: 1.5,
          py: 0.5,
          bgcolor: alpha(recColor, 0.1),
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: recColor, flexShrink: 0 }} />
        <Typography variant="caption" noWrap sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
          {recLabel}
        </Typography>
        {videoRecordings.length > 1 && (
          <Chip
            label={`${activeRecs.length > 1 ? `1 of ${activeRecs.length}` : ''} ${videoRecordings.length} recordings`}
            size="small"
            sx={{ height: 18, fontSize: '0.7rem', ml: 'auto', px: 0.5 }}
          />
        )}
      </Stack>

      {/* Video area */}
      <Box sx={{ flex: 1, position: 'relative', minHeight: 0 }}>
        {hasError && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              cursor: 'pointer',
            }}
            onClick={() => handleRetry(activeRec.id)}
          >
            <Typography color="error" variant="body2">
              Failed to load video
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              Click to retry
            </Typography>
          </Box>
        )}

        {isLoading && !hasError && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography color="text.secondary" variant="body2">
              Loading video...
            </Typography>
          </Box>
        )}

        {activeUrl && !hasError && (
          <ReactPlayer
            ref={playerRef}
            url={activeUrl}
            playing={isPlaying && isInRange}
            width="100%"
            height="100%"
            onError={handleError}
            onProgress={handleProgress}
            progressInterval={250}
            controls={false}
          />
        )}
      </Box>
    </Box>
  );
}
